import { useEffect, useState } from "react";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { checkSlugAvailable } from "../services/formLinkService";

/**
 * Where the check on the slug being typed has got to.
 *
 * `failed` is told apart from `idle` because something downstream acts on the
 * difference: Save waits for a verdict, and a question the server could not
 * answer is not a verdict it should go on waiting for. `idle` stays what it
 * says — nothing has been asked, because there is nothing worth asking about.
 */
export type SlugCheck = "idle" | "checking" | "free" | "taken" | "failed";

/** Long enough that a name is not asked about letter by letter, short enough
 *  that the answer is there by the time the typing stops. */
const DEBOUNCE_MS = 400;

/**
 * Asks whether a slug is free while it is being typed.
 *
 * Debounced and cancellable: every keystroke replaces the pending question, and
 * a request already in flight is aborted rather than left to answer about a
 * slug nobody is typing any more. Without that, a slow reply about `diksha-k`
 * could land after a fast one about `diksha-kumari` and mark the wrong name
 * taken.
 *
 * A failed check answers `failed`, never `taken`. The check is a courtesy —
 * the claim is what actually decides — so a server that cannot answer it must
 * not be able to stop somebody claiming a slug that is theirs for the taking.
 * The `PATCH` will reject it if it really is gone, and that rejection is
 * handled.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useSlugAvailability(slug: string, enabled: boolean): SlugCheck {
  const getToken = useTokenGetter();
  const [check, setCheck] = useState<SlugCheck>("idle");

  useEffect(() => {
    if (!enabled || !slug) {
      setCheck("idle");
      return;
    }

    setCheck("checking");

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const free = await checkSlugAvailable(getToken, slug, controller.signal);
        if (!controller.signal.aborted) setCheck(free ? "free" : "taken");
      } catch (error) {
        if (controller.signal.aborted) return;
        // Logged, not shown. A toast for every failed keystroke would bury the
        // screen, and the claim below still reports for itself.
        console.error("slug availability check failed:", error);
        setCheck("failed");
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [slug, enabled, getToken]);

  return check;
}

export default useSlugAvailability;
